/**
 * What « Planifier les 7 jours suivants » draws between the tap and the last
 * dinner (#851).
 *
 * The generator holds the main thread for about a second on a laptop and 2-3 s
 * on an iPad, and it used to run inside the tap: the screen froze, the card's
 * spinner never painted, and seven dinners appeared at once. Now the evenings
 * the gesture will fill turn into placeholders the moment the cook taps, and
 * once the dinners are in, each placeholder becomes its dinner in turn, top to
 * bottom.
 *
 * Pure, so the order of things is tested without rendering anything.
 */
import { addDays } from '../../../../utils/localDate';
import { HORIZON_DAYS } from '../../../../services/horizonDays';

export interface PlanArrival {
  /** The evenings that had no dinner at the tap, in order: these wait. */
  days: string[];
  /** The evenings that got a dinner, in order; null while they are chosen. */
  landed: string[] | null;
  /** How many of `landed` are drawn so far. */
  shown: number;
  /** False under « reduce motion »: no wave, and every dinner lands at once. */
  animate: boolean;
}

/** `pending` draws a placeholder, `rising` the dinner coming in, null the day as usual. */
export type ArrivalPhase = 'pending' | 'rising' | null;

/** The evenings of the seven days from `from` that have no dinner yet. */
export function emptyEvenings(from: string, dinnerDates: Iterable<string>): string[] {
  const planned = new Set(dinnerDates);
  return Array.from({ length: HORIZON_DAYS }, (_, i) => addDays(from, i)).filter((d) => !planned.has(d));
}

export function startArrival(days: string[], animate: boolean): PlanArrival {
  return { days, landed: null, shown: 0, animate };
}

/**
 * The dinners are in. Only the evenings that were waiting can rise: a dinner a
 * partner planned meanwhile was never a placeholder. An evening the generator
 * could not fill goes back to its « Ajouter un souper » row.
 */
export function landArrival(arrival: PlanArrival, dates: string[]): PlanArrival {
  const got = new Set(dates);
  const landed = arrival.days.filter((d) => got.has(d));
  return { ...arrival, landed, shown: arrival.animate ? 0 : landed.length };
}

/** One more dinner in. */
export function advanceArrival(arrival: PlanArrival): PlanArrival {
  if (!arrival.landed) return arrival;
  return { ...arrival, shown: Math.min(arrival.shown + 1, arrival.landed.length) };
}

export function arrivalDone(arrival: PlanArrival): boolean {
  return !!arrival.landed && arrival.shown >= arrival.landed.length;
}

export function arrivalPhase(arrival: PlanArrival | null | undefined, day: string): ArrivalPhase {
  if (!arrival) return null;
  if (!arrival.landed) return arrival.days.includes(day) ? 'pending' : null;
  const at = arrival.landed.indexOf(day);
  if (at < 0) return null;
  return at < arrival.shown ? 'rising' : 'pending';
}

/** Where a waiting evening sits in the wave, so the pulse runs down the list. */
export function arrivalIndex(arrival: PlanArrival | null | undefined, day: string): number {
  return Math.max(0, arrival?.days.indexOf(day) ?? 0);
}
